import { useRef } from 'react';

import { FiveSteps } from '@/lib/widgets/main-page/we-build-ourselves/FiveSteps';

import { Button } from '@/lib/shared/components/Buttons';
import { modal, FeedBackModalNew } from '@/lib/shared/components/Modal';
import { H2 } from '@/lib/shared/components/Typography';
import { useObserver } from '@/lib/shared/hooks/use-observer';
import { useWindowSize } from '@/lib/shared/hooks/use-window-size';

import cn from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './ResolvingQuestions.module.scss';

type ResolvingQuestionsProps = {
  theme?: 'light' | 'dark';
  title?: string;
  withButton?: boolean;
  video: {
    srcDesctop: string;
    srcMobile: string;
  };
  isVillaLanding?: boolean;
  isNewStep3?: boolean;
  customWhatsappUrl?: string;
};

export const ResolvingQuestions = ({
  theme = 'dark',
  video,
  title,
  withButton = true,
  isVillaLanding,
  isNewStep3,
  customWhatsappUrl,
}: ResolvingQuestionsProps) => {
  const { t } = useTranslation('index');
  const ref = useRef<HTMLDivElement>(null);
  const { isMobile } = useWindowSize();
  const isInterTrue = () => {
    if (ref.current) {
      const video = ref.current.querySelector('video');
      const sources = video?.querySelectorAll('source');
      if (sources?.length) {
        let source;
        if (isMobile) {
          source = sources[1];
        } else {
          source = sources[0];
        }
        if (source.src) return;
        source.src = source.dataset.src as string;
        video?.load();
      }
    }
  };
  useObserver({ ref, isInterTrue, isOnce: true });

  const openModal = () => {
    modal.open(
      <FeedBackModalNew
        leadType="SALE"
        title="signUpForConsultation"
        sourceForm="five steps form"
        eventName={['sale']}
        customWhatsappUrl={customWhatsappUrl}
      />,
      { isFeedBackNew: true }
    );
  };
  const buttonTheme = theme === 'dark' ? 'light-invert' : 'dark';
  return (
    <div className={cn(styles.container, styles.questions)} id={'5_steps'}>
      <H2
        className={cn(
          styles.questions__title,
          theme === 'light'
            ? styles['questions__title--light']
            : styles['questions__title--dark']
        )}
      >
        {title ? title : t('fiveStep.titleLanding')}
      </H2>
      <div
        className={cn(
          styles.questions__mainBlock,
          isVillaLanding && styles['questions__mainBlock--villa']
        )}
      >
        <div
          className={cn(
            styles.videoBlock,
            isVillaLanding && styles['videoBlock--big']
          )}
          ref={ref}
        >
          <video
            className={styles.videoContainer}
            autoPlay
            muted
            playsInline
            loop
          >
            <source
              media="(min-width: 768px)"
              data-src={video.srcDesctop}
              type="video/mp4"
            />
            <source
              media="(max-width: 768px)"
              data-src={video.srcMobile}
              type="video/mp4"
            />
          </video>
        </div>
        <FiveSteps
          buttonText={
            withButton ? t('landing.scheduleForConsultation') : undefined
          }
          buttonOnClick={withButton ? openModal : undefined}
          theme={theme}
          isLanding={true}
          isNewStep3={isNewStep3}
        />
      </div>
      {withButton && (
        <Button
          as={'button'}
          label={t('landing.scheduleForConsultation')}
          theme={buttonTheme}
          size={'lg'}
          className={styles.questions__button}
          onClick={openModal}
        />
      )}
    </div>
  );
};
